// @ts-check
// Formatação das conversas (doc 06 de arquitetura, seção 6): telefone E.164, separadores de data,
// hora relativa da lista, tamanho de arquivo e rótulos de prévia por tipo de mensagem.
import { LOCALE, t } from '../i18n/index.js';
import { formatTime as formatPreferredTime } from './format.js';

// hora em 12 ou 24 horas conforme a preferência, no fuso do aparelho (o mesmo dos separadores de dia)
const timeFormatter = { format: (/** @type {Date} */ date) => formatPreferredTime(date, { timeZone: 'device' }) };

const dayMonthFormatter = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long' });
const dayMonthYearFormatter = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
const shortDayMonthFormatter = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit' });
const shortDateFormatter = new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: '2-digit' });
const fullDateTimeFormatter = new Intl.DateTimeFormat(LOCALE, { dateStyle: 'full', timeStyle: 'short' });
const mediumDateTimeFormatter = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const DAY_MILLISECONDS = 86_400_000;
const KILOBYTE = 1024;

/**
 * Telefone do contato no formato brasileiro: 5511987654321 vira +55 (11) 98765-4321.
 * O `wa_id` da Meta pode vir sem o nono dígito (12 dígitos), também formatado.
 * @param {string | null | undefined} handle
 */
export function formatPhoneHandle(handle) {
  if (!handle) return '';
  const digits = String(handle).replace(/\D/g, '');
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    const areaCode = digits.slice(2, 4);
    const local = digits.slice(4);
    return `+55 (${areaCode}) ${local.slice(0, local.length - 4)}-${local.slice(-4)}`;
  }
  return digits ? `+${digits}` : String(handle);
}

/**
 * Normaliza o que o usuário digitou para o handle E.164 sem "+"; números sem DDI ganham 55.
 * @param {string} input
 */
export function toPhoneHandle(input) {
  const digits = String(input ?? '').replace(/\D/g, '');
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
}

/**
 * @param {string} input
 */
export function isValidPhoneInput(input) {
  const handle = toPhoneHandle(input);
  return handle.length >= 12 && handle.length <= 15;
}

/**
 * @param {string} input
 */
export function isValidEmail(input) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(input ?? '').trim());
}

/**
 * @param {Date} date
 */
function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Diferença em dias de calendário locais (0 = hoje, 1 = ontem).
 * @param {string | Date} value
 * @param {Date} [now]
 */
export function getCalendarDayDistance(value, now = new Date()) {
  const date = new Date(value);
  return Math.round((startOfDay(now) - startOfDay(date)) / DAY_MILLISECONDS);
}

/**
 * Chave do dia local, para agrupar mensagens por data.
 * @param {string} value
 */
export function getDayKey(value) {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * Separador de data da conversa: "Hoje", "Ontem", "12 de outubro" (com ano quando não é o atual).
 * @param {string} value
 * @param {Date} [now]
 */
export function formatDaySeparator(value, now = new Date()) {
  const distance = getCalendarDayDistance(value, now);
  if (distance === 0) return t('conversations.today');
  if (distance === 1) return t('conversations.yesterday');
  const date = new Date(value);
  return date.getFullYear() === now.getFullYear() ? dayMonthFormatter.format(date) : dayMonthYearFormatter.format(date);
}

/**
 * @param {string | null | undefined} value
 */
export function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : timeFormatter.format(date);
}

/**
 * Hora da lista de conversas: hora de hoje, "Ontem", dia e mês no ano corrente, data curta antes disso.
 * @param {string | null | undefined} value
 * @param {Date} [now]
 */
export function formatThreadListTime(value, now = new Date()) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const distance = getCalendarDayDistance(date, now);
  if (distance <= 0) return timeFormatter.format(date);
  if (distance === 1) return t('conversations.yesterday');
  return date.getFullYear() === now.getFullYear() ? shortDayMonthFormatter.format(date) : shortDateFormatter.format(date);
}

/**
 * @param {string | null | undefined} value
 */
export function formatFullDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : fullDateTimeFormatter.format(date);
}

/**
 * @param {string | null | undefined} value
 */
export function formatMediumDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : mediumDateTimeFormatter.format(date);
}

/**
 * Texto do aviso de janela aberta: só a hora quando expira hoje, data e hora quando expira amanhã.
 * @param {string} expiresAt
 * @param {Date} [now]
 */
export function formatServiceWindowExpiry(expiresAt, now = new Date()) {
  const date = new Date(expiresAt);
  const time = timeFormatter.format(date);
  if (getCalendarDayDistance(date, now) === 0) return t('conversations.window.openUntil', { time });
  const dayLabel = getCalendarDayDistance(date, now) === -1 ? t('conversations.window.tomorrow') : shortDayMonthFormatter.format(date);
  return t('conversations.window.openUntilDate', { date: dayLabel, time });
}

/**
 * @param {number | null | undefined} size bytes
 */
export function formatFileSize(size) {
  if (size === null || size === undefined || Number.isNaN(Number(size))) return '';
  const bytes = Number(size);
  if (bytes < KILOBYTE) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / KILOBYTE;
  let unitIndex = 0;
  while (value >= KILOBYTE && unitIndex < units.length - 1) {
    value /= KILOBYTE;
    unitIndex += 1;
  }
  const formatted = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value);
  return `${formatted} ${units[unitIndex]}`;
}

/**
 * Duração de áudio em m:ss.
 * @param {number} seconds
 */
export function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const rounded = Math.floor(seconds);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
}

/**
 * Categoria visual do arquivo pela extensão (doc 10 do mapeamento, 10.2).
 * @param {string | null | undefined} fileName
 * @param {string | null | undefined} [mimeType]
 * @returns {{ icon: string, color: string }}
 */
export function getFileVisual(fileName, mimeType) {
  const extension = String(fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
  const mime = String(mimeType ?? '');
  if (extension === 'pdf' || mime === 'application/pdf') return { icon: 'IconFileTypePdf', color: 'red' };
  if (['doc', 'docx', 'odt', 'txt', 'rtf', 'pages'].includes(extension)) return { icon: 'IconFileText', color: 'blue' };
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(extension)) return { icon: 'IconTable', color: 'turquoise' };
  if (['ppt', 'pptx', 'odp', 'key'].includes(extension)) return { icon: 'IconPresentation', color: 'orange' };
  if (mime.startsWith('image/')) return { icon: 'IconPhoto', color: 'yellow' };
  if (mime.startsWith('video/')) return { icon: 'IconVideo', color: 'purple' };
  if (mime.startsWith('audio/')) return { icon: 'IconHeadphones', color: 'pink' };
  if (['zip', 'rar', '7z', 'gz', 'tar'].includes(extension)) return { icon: 'IconFileZip', color: 'gray' };
  return { icon: 'IconFile', color: 'gray' };
}

/**
 * Tipo da mensagem pelo primeiro anexo, para a prévia da lista e o rótulo de anexos.
 * @param {string | null | undefined} mimeType
 * @returns {'IMAGE' | 'AUDIO' | 'VIDEO' | 'DOCUMENT'}
 */
export function getAttachmentKindFromMimeType(mimeType) {
  const mime = String(mimeType ?? '');
  if (mime.startsWith('image/')) return 'IMAGE';
  if (mime.startsWith('audio/')) return 'AUDIO';
  if (mime.startsWith('video/')) return 'VIDEO';
  return 'DOCUMENT';
}

/**
 * Rótulo da prévia quando a última mensagem não tem texto.
 * @param {string | null | undefined} kind
 */
export function getMessageKindLabel(kind) {
  const key = kind && ['IMAGE', 'AUDIO', 'VOICE_NOTE', 'VIDEO', 'DOCUMENT', 'STICKER'].includes(kind) ? kind : 'TEXT';
  return t(`conversations.kinds.${key}`);
}

/**
 * @param {string | null | undefined} kind
 */
export function getMessageKindIcon(kind) {
  switch (kind) {
    case 'IMAGE':
      return 'IconPhoto';
    case 'AUDIO':
      return 'IconHeadphones';
    case 'VOICE_NOTE':
      return 'IconMicrophone';
    case 'VIDEO':
      return 'IconVideo';
    case 'DOCUMENT':
      return 'IconFileText';
    case 'STICKER':
      return 'IconMoodSmile';
    default:
      return null;
  }
}

/**
 * Texto de uma linha a partir do HTML do e-mail, para mensagens recolhidas.
 * @param {string | null | undefined} text
 * @param {string | null | undefined} htmlContent
 */
export function getMessageSnippet(text, htmlContent) {
  if (text && text.trim()) return text.replace(/\s+/g, ' ').trim();
  if (!htmlContent) return '';
  const documentFragment = new DOMParser().parseFromString(htmlContent, 'text/html');
  return (documentFragment.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Assunto da resposta: acrescenta "Re:" só quando ainda não existe.
 * @param {string | null | undefined} subject
 */
export function buildReplySubject(subject) {
  const base = String(subject ?? '').trim();
  if (/^re:/i.test(base)) return base;
  return `${t('conversations.email.replyPrefix')} ${base}`.trim();
}

const MINUTE_MILLISECONDS = 60_000;
const HOUR_MILLISECONDS = 60 * MINUTE_MILLISECONDS;
// a partir daqui a espera aparece em tom de alerta na lista
export const AWAITING_REPLY_ALERT_MILLISECONDS = HOUR_MILLISECONDS;

/**
 * Duração curta desde um instante ("12 min", "2 h", "3 d"), para o tempo de espera por resposta.
 * @param {string | null | undefined} since
 * @param {Date} [now]
 * @returns {{ label: string, milliseconds: number } | null}
 */
export function formatWaitingDuration(since, now = new Date()) {
  if (!since) return null;
  const date = new Date(since);
  if (Number.isNaN(date.getTime())) return null;
  const milliseconds = Math.max(0, now.getTime() - date.getTime());
  if (milliseconds < HOUR_MILLISECONDS) {
    return { label: t('conversations.duration.minutes', { count: Math.max(1, Math.floor(milliseconds / MINUTE_MILLISECONDS)) }), milliseconds };
  }
  if (milliseconds < DAY_MILLISECONDS) return { label: t('conversations.duration.hours', { count: Math.floor(milliseconds / HOUR_MILLISECONDS) }), milliseconds };
  return { label: t('conversations.duration.days', { count: Math.floor(milliseconds / DAY_MILLISECONDS) }), milliseconds };
}
